import { useContext, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import * as dayjs from 'dayjs';
import * as isoWeek from 'dayjs/plugin/isoWeek';

// Context.
import { InstanceContext } from '../../context/InstanceContext';
import { FilterContext } from '../../context/FilterContext';

// Api.
import StatisticApi from '../../api/instance/StatisticApi';

// Types.
import InstancesDataTable from '../../types/instance/tables/InstancesDataTable';

// Constants.
import { DATA_INITIALISING, DATA_LOADING, DATA_LOADED } from '../../component/Constants';

// Helpers.
import { getFilterParameters } from '../../helpers/utils';
import Helper from '../../helpers/Helper';

// Components.
import InstancesTable from '../../component/instance/table/InstancesTable';
import { useParams } from "react-router-dom";
import HostsTabbed from "./HostsTabbed";

import HostTarget from "../../types/host/HostTarget";
import HostApi from "../../api/host/HostApi";
import HostInstances from "../../types/host/HostInstances";
import { TimeRangeContext } from "../../context/TimeRangeContext";
import EventsApi from "../../api/instance/EventsApi";
import CombinedChanges from "../../types/instance/CombinedChanges";
// DayJS plugins - note, the position of these in this file are important.
dayjs.extend(isoWeek.default);


function List(props: { toggleMenu: Function, setFilterOptions: Function, hosts: HostTarget[] }) {
    const { instances } = useContext(InstanceContext);

    const [combinedChanges, setCombinedChanges] = useState<CombinedChanges[]>([]);
    const { hosts } = props
    const { hostId } = useParams();

    const [loading, setLoading] = useState<number>(DATA_LOADING);
    const [tableLoading, setTableLoading] = useState<number>(DATA_LOADING);
    const [instancesDataTable, setInstancesDataTable] = useState<InstancesDataTable[]>([]);
    const [hostInstances, setHostInstances] = useState<HostInstances[]>([]);

    const timeRangeContext = useContext(TimeRangeContext)
    const { currentFilterOptions } = useContext(FilterContext);
    const filterParameters = getFilterParameters(currentFilterOptions);

    const host = hosts.find(item => item.id === Number(hostId))

    useMemo(() => {
        const getHostInstances = async () => {
            setLoading(DATA_LOADING);

            HostApi.getHostInstances(Number(hostId)).then((instances: HostInstances[]) => {
                setLoading(DATA_LOADED);
                setHostInstances(instances);
            })
        }
        getHostInstances();
    }, [hostId]);


    useMemo(() => {
        const getChanges = async() => {

            // Get the event types, known, detected changes and registered events.
            const allEvents: any[] = await EventsApi.getAllEventsChangesAndAlerts(timeRangeContext);
            let combinedChanges: CombinedChanges[] = []

            for (let index = 0; index < allEvents.length; index++) {
                const currentItem = allEvents[index];
                combinedChanges.push({
                    id: index,
                    instanceId: currentItem.datasourceid,
                    timeslice: currentItem.timeslice,
                    [currentItem.event]: currentItem
                });
            }

            // Set the sort order for the combined changes and events.
            combinedChanges.sort(function compare(a, b) {
                var aa: number = Helper.parseTimesliceWithTz(String(a.timeslice)).valueOf();
                var bb: number = Helper.parseTimesliceWithTz(String(b.timeslice)).valueOf();
                return bb - aa;
            });

            setCombinedChanges(combinedChanges)
        }
        void getChanges();

    }, [instances, filterParameters, hostInstances]);

    useMemo(() => {


        const getInstances = async () => {
            let instancesDataTable: InstancesDataTable[] = [];
            const instancesIdsArray = hostInstances.map(a => a.id);
            const instancesToDisplay = instances.filter(instance => instancesIdsArray.includes(instance.id));
            
            if (instancesToDisplay.length) {
                for (const instance of instancesToDisplay) {
                    // Build the initial hosts table.
                    instancesDataTable.push({
                        id: instance.id,
                        name: instance.name,
                        type: instance.type,
                        status: instance.status,
                        color: instance.color,
                        changes: null,
                        alerts: null,
                        customEvents: null,
                        time: null,
                        executions: null,
                        averageTime: 0,
                        previousTime: null,
                        previousExecutions: null,
                        previousAverageTime: null,
                        instance: instance
                    });
                }

                const promisesTime = []
                const promisesExecutions = []

                for (let index = 0; index < instancesDataTable.length; index++) {
                    const currentInstanceChanges =  combinedChanges.filter(change => change.instanceId === instancesDataTable[index].id)

                    instancesDataTable[index].changes = currentInstanceChanges.filter(item => item.change).length;
                    instancesDataTable[index].customEvents = currentInstanceChanges.filter(item => item.event).length
                    instancesDataTable[index].alerts = currentInstanceChanges.filter(item => item.alert).length

                    promisesTime.push(StatisticApi.getTimeStatistic(timeRangeContext, instancesDataTable[index].id, filterParameters))
                    promisesExecutions.push(StatisticApi.getStatistic(timeRangeContext, instancesDataTable[index].id, 'executions', filterParameters))
                }

                const asyncTimeResponses = await Promise.all(promisesTime)
                asyncTimeResponses.forEach((timeResponse: number, index: number) => {
                    instancesDataTable[index].time = timeResponse;
                })

                const asyncExecutionsResponses = await Promise.all(promisesExecutions)
                asyncExecutionsResponses.forEach((executionResponse: number, index: number) => {
                    instancesDataTable[index].executions = executionResponse;
                })

                instancesDataTable.forEach((instance, index) => {
                    // @ts-ignore
                    let averageTime = parseInt((instancesDataTable[index].time / instancesDataTable[index].executions).toFixed(2));
                    averageTime = isNaN(averageTime) || averageTime === Infinity || averageTime === -Infinity ? 0 : averageTime
                    instance.averageTime = averageTime
                })
                if (instancesDataTable.length) {
                    setInstancesDataTable(instancesDataTable)
                }
            }
            setTableLoading(DATA_LOADED)
        }

        void getInstances();

    }, [combinedChanges, filterParameters]);

    function refresh() {
        setLoading(DATA_INITIALISING);
    }

    return (
        <HostsTabbed
            hosts={props.hosts}
            toggleMenu={props.toggleMenu}
           
            setFilterOptions={props.setFilterOptions}
            refresh={refresh}
            loading={loading}
            activeTab="instances"
            title={`${host?.name} - Database Instances`}
            description={`List of all monitored DB instances with high-level performance statistics for ${host?.name}`}
        >

            {/* Instances */}
            <div className="row row-cols-1">
                <div className="col">
                    <div className="card collapsible">
                        <div className="card-header">
                            <i className="fal fa-database fa-fw" aria-hidden="true"/>
                            Monitored instances on host {host?.name}
                            <span className="badge bg-info"
                                  data-tooltip-id="global-tooltip" data-tooltip-content="Total instances count">{instancesDataTable.length.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}</span>
                            <i className="collapse-toggle" role="button" data-bs-toggle="collapse"
                               data-bs-target="#collapseInstances" aria-expanded="false"
                               aria-controls="collapseInstances"/>
                        </div>
                        <div id="collapseInstances" className="card-body collapse show">
                            <InstancesTable tags={[]} error={null} loading={tableLoading}
                                            instancesDataTable={instancesDataTable}/>
                        </div>
                    </div>
                </div>
            </div>
        </HostsTabbed>
    )
}

List.propTypes = {
    toggleMenu: PropTypes.func.isRequired,
};

export default List;
